import { CalendarBlankIcon, PowerIcon, TimerIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { hasPowerControl, powerActionLabel } from '../format';
import type { Device } from '../types';
import { useCommandLifecycle } from '../use-command-lifecycle';

import styles from './device-control.module.scss';
import { ScheduleDrawer } from './schedule-drawer';
import { StopTimerDrawer } from './stop-timer-drawer';

export type DeviceControlProps = {
  device: Device;
};

/**
 * Device control cluster (Figma 2058-449993): a dark pill with Stop Timer (left),
 * a large lime power button (center), and Schedule (right). Stop Timer is enabled only
 * when the device is Active; the power button issues turn_on/turn_off and runs the
 * command lifecycle (story 101849). Electric pumps only show the power button; sensors
 * render no controls.
 */
export function DeviceControl({ device }: DeviceControlProps) {
  const { pending, run } = useCommandLifecycle(device);
  const [stopOpen, setStopOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  if (device.kind !== 'pump') return null;

  const showPower = hasPowerControl(device);
  const isActive = device.status === 'active';
  const turningOff = powerActionLabel(device) === 'Off';

  return (
    <div className={styles.wrap}>
      <div className={styles.pill}>
        <button
          type="button"
          className={styles.side}
          disabled={!isActive || pending}
          onClick={() => setStopOpen(true)}
        >
          <TimerIcon size={18} />
          <span>Stop Timer</span>
        </button>

        {showPower ? (
          <button
            type="button"
            className={[styles.power, pending ? styles.powerPending : undefined]
              .filter(Boolean)
              .join(' ')}
            disabled={pending}
            aria-label={pending ? 'Command queued' : `Turn ${powerActionLabel(device)}`}
            title={pending ? 'Action queued…' : `Turn ${powerActionLabel(device)}`}
            onClick={() => run(turningOff ? 'turn_off' : 'turn_on')}
          >
            <PowerIcon size={26} weight="bold" />
          </button>
        ) : null}

        <button
          type="button"
          className={styles.side}
          disabled={pending}
          onClick={() => setScheduleOpen(true)}
        >
          <CalendarBlankIcon size={18} />
          <span>Schedule</span>
        </button>
      </div>

      <StopTimerDrawer
        open={stopOpen}
        onClose={() => setStopOpen(false)}
        onConfirm={() => {
          setStopOpen(false);
          run('stop_timer');
        }}
      />
      <ScheduleDrawer
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onConfirm={() => {
          setScheduleOpen(false);
          run('schedule');
        }}
      />
    </div>
  );
}
